<template>
  <div class="bar-chart-scroll">
    <div class="bar-chart-container">
      <canvas ref="canvas"></canvas>
    </div>
  </div>
</template>

<script>
import { Bar } from 'vue-chartjs';

export default {
  extends: Bar,
  name: 'TemplateEventsWhatsAppStatusChart',
  props: {
    templateEvents: {
      type: Array,
      required: true,
    },
  },
  methods: {
    updateChart() {
      const statusTypes = ['queued', 'sent', 'delivered', 'failed'];
      const colorMap = {
        queued: '#616161',
        sent: '#4dd0e1',
        delivered: '#2e7d32',
        failed: '#e53935',
      };

      const grouped = {};
      this.templateEvents.forEach(event => {
        const date = new Date(event.created_at).toLocaleDateString('pt-BR', {
          day: 'numeric',
          month: 'short',
          year: 'numeric',
        });
        const status = event.status || 'queued';
        if (!grouped[date]) {
          grouped[date] = {};
        }
        grouped[date][status] = (grouped[date][status] || 0) + 1;
      });

      const labels = Object.keys(grouped).filter(date => {
        const statuses = grouped[date];
        const total = statusTypes.reduce(
          (sum, status) => sum + (statuses[status] || 0),
          0
        );
        return total > 0;
      });

      const datasets = statusTypes.map(status => ({
        label: this.statusLabel(status),
        backgroundColor: colorMap[status],
        data: labels.map(date => grouped[date]?.[status] || 0),
        stack: 'status',
      }));

      const hasData = datasets.some(ds => ds.data.some(val => val > 0));
      const finalLabels = hasData
        ? labels
        : [this.$t('TEMPLATE_EVENTS_REPORT.UNKNOWN')];
      const finalDatasets = hasData
        ? datasets
        : [
            {
              label: this.$t('TEMPLATE_EVENTS_REPORT.UNKNOWN'),
              backgroundColor: '#ccc',
              data: [0],
              stack: 'status',
            },
          ];

      this.renderChart(
        {
          labels: finalLabels,
          datasets: finalDatasets,
        },
        {
          responsive: true,
          maintainAspectRatio: false,
          scales: {
            xAxes: [
              {
                stacked: true,
                ticks: {
                  fontSize: 12,
                },
              },
            ],
            yAxes: [
              {
                stacked: true,
                ticks: {
                  beginAtZero: true,
                  fontSize: 12,
                  callback: value =>
                    value >= 1000 ? `${value / 1000} mil` : value,
                },
              },
            ],
          },
          legend: {
            labels: {
              fontSize: 12,
            },
          },
          tooltips: {
            mode: 'index',
            intersect: false,
            callbacks: {
              label: (tooltipItem, data) => {
                const dataset = data.datasets[tooltipItem.datasetIndex];
                const label = dataset.label || '';
                const value = tooltipItem.value || 0;
                if (Number(value) === 0) return '';
                return `${label}: ${Number(value).toLocaleString('pt-BR')}`;
              },
            },
          },
        }
      );
    },

    statusLabel(status) {
      switch (status) {
        case 'queued':
          return this.$t('TEMPLATE_EVENTS_REPORT.STATUS.QUEUED');
        case 'sent':
          return this.$t('TEMPLATE_EVENTS_REPORT.STATUS.SENT');
        case 'delivered':
          return this.$t('TEMPLATE_EVENTS_REPORT.STATUS.DELIVERED');
        case 'failed':
          return this.$t('TEMPLATE_EVENTS_REPORT.STATUS.FAILED');
        default:
          return this.$t('TEMPLATE_EVENTS_REPORT.STATUS.UNKNOWN');
      }
    },
  },

  watch: {
    templateEvents: {
      handler() {
        this.updateChart();
      },
    },
  },

  mounted() {
    this.updateChart();
  },
};
</script>

<style scoped>
.bar-chart-scroll {
  overflow-x: auto;
  width: 100%;
}

.bar-chart-container {
  position: relative;
  height: 300px;
  width: 100%;
  min-width: 600px;
}
</style>
